import React, { useEffect, useState } from 'react';
import { RouteComponentProps, withRouter, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import RestService from '../Services/RestService';
import OKomLoading from '../Components/OsloKommuneUI/OKomLoading';
import PktAlert from '../Components/PktAlert';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NO_MOBILE_REGEX = /^[49]\d{7}$/;

const RegisterView: React.FC<RouteComponentProps> = () => {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [departmentId, setDepartmentId] = useState(0);
  const [departments, setDepartments] = useState<Api.Department[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await RestService.get<Api.Department[]>('/api/examregistration/GetDepartments');
        setDepartments(res);
      } catch {
        toast.error('Kunne ikke laste avdelinger');
      }
    })();
  }, []);

  const validate = (): string | null => {
    if (!email.trim()) return 'E-postadresse er påkrevd';
    if (!EMAIL_REGEX.test(email.trim())) return 'Ugyldig e-postadresse';
    if (!mobile.trim()) return 'Mobilnummer er påkrevd';
    if (!NO_MOBILE_REGEX.test(mobile.trim())) return 'Mobilnummer må være 8 siffer og starte med 4 eller 9 (uten +47)';
    if (!departmentId || departmentId <= 0) return 'Velg en avdeling';
    return null;
  };

  const handleSubmit = async () => {
    const v = validate();
    if (v) { setError(v); return; }
    setError(null);
    try {
      setIsLoading(true);
      const response = await RestService.post<Api.RegisterRequest, Api.LoginResponse>(
        '/api/login/Register',
        { Email: email.trim(), MobilePhone: mobile.trim(), DepartmentId: departmentId },
      );
      if (response.Success) {
        toast.success('Pinkode sendt til ' + email);
        history.push(`/login?email=${encodeURIComponent(email.trim())}`);
      } else {
        setError(response.Message ?? 'Kunne ikke registrere');
      }
    } catch (e) {
      setError(`Noe gikk galt: ${e}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {isLoading && <OKomLoading text='Registrerer, vennligst vent...' />}
      <div className="ods-margin-top-25 ods-grid ods-grid--gap ods-grid--center">
        <div className="ods-grid__column--12 ods-grid__column--start-3-breakpoint-medium ods-grid__column--end-10-breakpoint-medium">
          <div className="ods-status-card">
            <div className="ods-status-card__content">
              <h3 className="ods-status-card__heading">REGISTRER NY KONTO</h3>

              <label className="ods-input__label ods-margin-top-11">E-postadresse
                <input
                  value={email}
                  onChange={e => { setEmail(e.target.value); setError(null); }}
                  onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); }}
                  className="ods-input__input"
                  type="email"
                  autoComplete="email"
                  placeholder="din@epost.no"
                />
              </label>

              <label className="ods-input__label ods-margin-top-3">Mobilnummer
                <input
                  value={mobile}
                  onChange={e => { setMobile(e.target.value); setError(null); }}
                  onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); }}
                  className="ods-input__input"
                  type="tel"
                  autoComplete="tel"
                  placeholder="8 siffer, uten +47"
                />
              </label>

              <label className="ods-input__label ods-margin-top-3">Avdeling
                <select
                  value={departmentId}
                  onChange={e => { setDepartmentId(parseInt(e.target.value, 10)); setError(null); }}
                  className="ods-input__input"
                >
                  <option value={0}>-- velg avdeling --</option>
                  {departments.map(d => (<option key={d.Id} value={d.Id}>{d.Name}</option>))}
                </select>
              </label>

              {error && (
                <div className="ods-margin-top-11">
                  <PktAlert title="Registrering mislyktes" skin="warning" compact>
                    <span>{error}</span>
                  </PktAlert>
                </div>
              )}

              <div className="ods-status-card__actions ods-margin-top-11">
                <div className="ods-text--align-center">
                  <button disabled={isLoading} className="ods-button" onClick={handleSubmit}>Registrer</button>
                </div>
              </div>

              <div className="ods-margin-top-11 ods-text--align-center">
                <a href="/login" className="o-kom-link">Har du allerede en konto? Logg inn</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default withRouter(RegisterView);
